import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

/**
 * Practice entity data model matching the backend API.
 */
export interface PracticeEntity {
  id: string;
  tenantId: string;
  name: string;
  code?: string;
  businessNumber?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    province?: string;
    postalCode?: string;
    country?: string;
  };
  phone?: string;
  email?: string;
  website?: string;
  branding: {
    logoUrl?: string;
    primaryColor?: string;
    secondaryColor?: string;
    favicon?: string;
  };
  invoicePrefix?: string;
  invoiceNextNumber?: number;
  defaultTaxRate?: number;
  defaultTaxType?: string;
  paymentTermsDays?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePracticeEntityPayload {
  name: string;
  code?: string;
  businessNumber?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    province?: string;
    postalCode?: string;
    country?: string;
  };
  phone?: string;
  email?: string;
  website?: string;
  branding?: {
    logoUrl?: string;
    primaryColor?: string;
    secondaryColor?: string;
    favicon?: string;
  };
  invoicePrefix?: string;
  defaultTaxRate?: number;
  defaultTaxType?: string;
  paymentTermsDays?: number;
}

export interface UpdatePracticeEntityPayload extends Partial<CreatePracticeEntityPayload> {
  isActive?: boolean;
}

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const practiceEntitiesKeys = {
  all: ['practice-entities'] as const,
  lists: () => [...practiceEntitiesKeys.all, 'list'] as const,
  list: (params?: { isActive?: boolean }) => [...practiceEntitiesKeys.lists(), params] as const,
  details: () => [...practiceEntitiesKeys.all, 'detail'] as const,
  detail: (id: string) => [...practiceEntitiesKeys.details(), id] as const,
};

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Transforms a snake_case-keyed object to camelCase keys (shallow).
 */
function toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
}

function toCamelCaseObj<T>(obj: Record<string, unknown>): T {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    result[toCamelCase(key)] = value;
  }
  return result as T;
}

/**
 * Converts a camelCase string to snake_case.
 */
function toSnakeCase(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

/**
 * Transforms a camelCase-keyed object to snake_case keys (shallow).
 * Strips keys with undefined values.
 */
function toSnakeCasePayload(obj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined) continue;
    result[toSnakeCase(key)] = value;
  }
  return result;
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Fetch all practice entities for the current tenant.
 */
export function usePracticeEntities(params?: { isActive?: boolean }) {
  return useQuery({
    queryKey: practiceEntitiesKeys.list(params),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Record<string, unknown>[]>>(
        '/practice-entities',
        {
          params: params?.isActive !== undefined ? { is_active: params.isActive } : undefined,
        },
      );
      const raw = response.data.data ?? [];
      return raw.map((entity) => toCamelCaseObj<PracticeEntity>(entity));
    },
  });
}

/**
 * Fetch a single practice entity by ID.
 */
export function usePracticeEntity(id: string) {
  return useQuery({
    queryKey: practiceEntitiesKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Record<string, unknown>>>(
        `/practice-entities/${id}`,
      );
      return toCamelCaseObj<PracticeEntity>(response.data.data);
    },
    enabled: !!id,
  });
}

/**
 * Create a new practice entity.
 */
export function useCreatePracticeEntity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreatePracticeEntityPayload) => {
      const body = toSnakeCasePayload(payload as unknown as Record<string, unknown>);
      const response = await apiClient.post<ApiResponse<Record<string, unknown>>>(
        '/practice-entities',
        body,
      );
      return toCamelCaseObj<PracticeEntity>(response.data.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: practiceEntitiesKeys.lists() });
    },
  });
}

/**
 * Update an existing practice entity.
 */
export function useUpdatePracticeEntity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...payload }: UpdatePracticeEntityPayload & { id: string }) => {
      const body = toSnakeCasePayload(payload as unknown as Record<string, unknown>);
      const response = await apiClient.put<ApiResponse<Record<string, unknown>>>(
        `/practice-entities/${id}`,
        body,
      );
      return toCamelCaseObj<PracticeEntity>(response.data.data);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: practiceEntitiesKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: practiceEntitiesKeys.detail(variables.id),
      });
    },
  });
}
